import type { Dispatch, Reducer } from 'react';
import { useEffect, useReducer, useRef, useState } from 'react';

import Logger from '@/lib/logger';
import {
  getPersistenceGeneration,
  isPersistenceWriteAllowed,
  registerPendingPersistenceWrite,
} from '@/utils/persistenceCoordinator';

export type StorageType = 'localStorage' | 'sessionStorage';

export enum ExpirationInterval {
  A_DAY = 'a day',
  A_WEEK = 'a week',
  A_MONTH = 'a month',
  A_YEAR = 'a year',
}

export type UsePersistentReducerOptionsType = {
  key: string;
  enabled: boolean;
  storageType?: StorageType;
  expiresAt?: ExpirationInterval | number;
  writeDelayMs?: number;
};

const DEFAULT_WRITE_DELAY_MS = process.env.NODE_ENV === 'test' ? 0 : 800;
const PERSISTENCE_KIND = 'atlas.persisted-reducer';
const PERSISTENCE_VERSION = 2;
const LEGACY_ENVELOPE_VERSION = 1;
const COMPATIBILITY_METADATA_KEY = '__atlasPersistence';

const EXPIRATION_INTERVALS: Record<ExpirationInterval, number> = {
  [ExpirationInterval.A_DAY]: 24 * 60 * 60 * 1000,
  [ExpirationInterval.A_WEEK]: 7 * 24 * 60 * 60 * 1000,
  [ExpirationInterval.A_MONTH]: 30 * 24 * 60 * 60 * 1000,
  [ExpirationInterval.A_YEAR]: 365 * 24 * 60 * 60 * 1000,
};

type PersistedEnvelope<S> = {
  kind: typeof PERSISTENCE_KIND;
  version: typeof PERSISTENCE_VERSION;
  revision: number;
  writtenAt: number;
  value: S;
  expiresAt?: number;
};

type CompatibilityMetadata = {
  schema: typeof PERSISTENCE_VERSION;
  revision: number;
  writtenAt: number;
};

type DecodedState<S> =
  | {
      status: 'valid';
      value: S;
      expiresAt?: number;
      revision: number;
      isCurrentBridge: boolean;
    }
  | { status: 'empty' | 'expired' | 'invalid' }
  | { status: 'unsupported' };

type HydrationResult<S> = {
  value: S;
  expiresAt?: number;
  revision: number;
  serializedValue?: string;
  writeBlocked: boolean;
  errors: unknown[];
};

const V2_ENVELOPE_KEYS = new Set([
  'kind',
  'version',
  'revision',
  'writtenAt',
  'value',
  'expiresAt',
]);
const V1_ENVELOPE_KEYS = new Set(['version', 'value', 'expiresAt']);
const RESERVED_LEGACY_KEYS = new Set([
  'kind',
  'version',
  'revision',
  'writtenAt',
  'value',
  'expiresAt',
  '__expirationTimestamp',
  COMPATIBILITY_METADATA_KEY,
]);

export function getCurrentPersistenceKey(key: string): string {
  return `${key}:v${PERSISTENCE_VERSION}`;
}

function getExpirationTime(expiresAt: ExpirationInterval | number): number {
  if (typeof expiresAt === 'number') {
    return expiresAt;
  }

  return Date.now() + EXPIRATION_INTERVALS[expiresAt];
}

function getStorage(storageType: StorageType): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window[storageType];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: ReadonlySet<string>
): boolean {
  return Object.keys(value).every((key) => allowedKeys.has(key));
}

function isExpired(expiresAt: unknown): boolean {
  return typeof expiresAt === 'number' && Date.now() > expiresAt;
}

function normalizeValue<S, I>(
  value: unknown,
  init: (arg?: I) => S
): S | undefined {
  try {
    return init(value as I);
  } catch {
    return undefined;
  }
}

function decodeCurrentState<S, I>(
  serialized: string | null,
  init: (arg?: I) => S,
  errors: unknown[]
): DecodedState<S> {
  if (!serialized) {
    return { status: 'empty' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch (error) {
    errors.push(error);
    return { status: 'invalid' };
  }

  if (
    isRecord(parsed) &&
    parsed.kind === PERSISTENCE_KIND &&
    parsed.version !== PERSISTENCE_VERSION
  ) {
    return { status: 'unsupported' };
  }

  if (
    !isRecord(parsed) ||
    parsed.kind !== PERSISTENCE_KIND ||
    parsed.version !== PERSISTENCE_VERSION ||
    !hasOnlyKeys(parsed, V2_ENVELOPE_KEYS) ||
    !('value' in parsed) ||
    !Number.isInteger(parsed.revision) ||
    typeof parsed.writtenAt !== 'number'
  ) {
    return { status: 'invalid' };
  }

  if (isExpired(parsed.expiresAt)) {
    return { status: 'expired' };
  }

  const value = normalizeValue<S, I>(parsed.value, init);
  if (value === undefined) {
    return { status: 'invalid' };
  }

  return {
    status: 'valid',
    value,
    expiresAt:
      typeof parsed.expiresAt === 'number' ? parsed.expiresAt : undefined,
    revision: parsed.revision as number,
    isCurrentBridge: true,
  };
}

function sanitizeLegacyValue(value: unknown): unknown {
  if (!isRecord(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).filter(([key]) => !RESERVED_LEGACY_KEYS.has(key))
  );
}

function decodeLegacyState<S, I>(
  serialized: string | null,
  init: (arg?: I) => S,
  errors: unknown[]
): DecodedState<S> {
  if (!serialized) {
    return { status: 'empty' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch (error) {
    errors.push(error);
    return { status: 'invalid' };
  }

  const compatibilityMetadata =
    isRecord(parsed) && isRecord(parsed[COMPATIBILITY_METADATA_KEY])
      ? (parsed[COMPATIBILITY_METADATA_KEY] as Record<string, unknown>)
      : undefined;

  if (
    typeof compatibilityMetadata?.schema === 'number' &&
    compatibilityMetadata.schema > PERSISTENCE_VERSION
  ) {
    return { status: 'unsupported' };
  }

  if (
    isRecord(parsed) &&
    ((parsed.kind === PERSISTENCE_KIND &&
      parsed.version !== PERSISTENCE_VERSION) ||
      (typeof parsed.version === 'number' &&
        parsed.version > PERSISTENCE_VERSION &&
        'value' in parsed))
  ) {
    return { status: 'unsupported' };
  }

  if (
    isRecord(parsed) &&
    parsed.version === LEGACY_ENVELOPE_VERSION &&
    'value' in parsed &&
    hasOnlyKeys(parsed, V1_ENVELOPE_KEYS)
  ) {
    if (isExpired(parsed.expiresAt)) {
      return { status: 'expired' };
    }

    const value = normalizeValue<S, I>(parsed.value, init);
    return value === undefined
      ? { status: 'invalid' }
      : {
          status: 'valid',
          value,
          expiresAt:
            typeof parsed.expiresAt === 'number' ? parsed.expiresAt : undefined,
          revision: 0,
          isCurrentBridge: false,
        };
  }

  if (isRecord(parsed) && isExpired(parsed.__expirationTimestamp)) {
    return { status: 'expired' };
  }

  const value = normalizeValue<S, I>(sanitizeLegacyValue(parsed), init);
  if (value === undefined) {
    return { status: 'invalid' };
  }

  const isCurrentBridge =
    compatibilityMetadata?.schema === PERSISTENCE_VERSION &&
    Number.isInteger(compatibilityMetadata.revision);

  return {
    status: 'valid',
    value,
    expiresAt:
      isRecord(parsed) && typeof parsed.__expirationTimestamp === 'number'
        ? parsed.__expirationTimestamp
        : undefined,
    revision: isCurrentBridge ? (compatibilityMetadata.revision as number) : 0,
    isCurrentBridge,
  };
}

function safelyInitialize<S, I>(init: (arg?: I) => S, fallback: S): S {
  try {
    return init(undefined);
  } catch {
    return fallback;
  }
}

function hydrateFromStorage<S, I>({
  enabled,
  key,
  storageType,
  init,
  fallback,
}: Required<Pick<UsePersistentReducerOptionsType, 'enabled' | 'key'>> & {
  storageType: StorageType;
  init: (arg?: I) => S;
  fallback: S;
}): HydrationResult<S> {
  const defaultValue = safelyInitialize(init, fallback);
  const storage = getStorage(storageType);
  if (!enabled || !storage) {
    return {
      value: defaultValue,
      revision: 0,
      serializedValue: JSON.stringify(defaultValue),
      writeBlocked: true,
      errors: [],
    };
  }

  const errors: unknown[] = [];
  let currentSerialized: string | null;
  let legacySerialized: string | null;
  try {
    currentSerialized = storage.getItem(getCurrentPersistenceKey(key));
    legacySerialized = storage.getItem(key);
  } catch (error) {
    return {
      value: defaultValue,
      revision: 0,
      serializedValue: JSON.stringify(defaultValue),
      writeBlocked: true,
      errors: [error],
    };
  }

  const current = decodeCurrentState(currentSerialized, init, errors);
  const legacy = decodeLegacyState(legacySerialized, init, errors);

  if (current.status === 'unsupported' || legacy.status === 'unsupported') {
    return {
      value: defaultValue,
      revision: 0,
      serializedValue: JSON.stringify(defaultValue),
      writeBlocked: true,
      errors,
    };
  }

  const currentValid = current.status === 'valid' ? current : undefined;
  const legacyValid = legacy.status === 'valid' ? legacy : undefined;
  const currentSerializedValue = currentValid
    ? JSON.stringify(currentValid.value)
    : undefined;
  const legacySerializedValue = legacyValid
    ? JSON.stringify(legacyValid.value)
    : undefined;

  let value = defaultValue;
  let expiresAt: number | undefined;

  if (currentValid && legacyValid) {
    // The flat bridge is authoritative whenever an older build changed it.
    if (currentSerializedValue === legacySerializedValue) {
      value = currentValid.value;
      expiresAt = currentValid.expiresAt ?? legacyValid.expiresAt;
    } else {
      value = legacyValid.value;
      expiresAt = legacyValid.expiresAt;
    }
  } else if (legacyValid) {
    value = legacyValid.value;
    expiresAt = legacyValid.expiresAt;
  }
  // A missing legacy bridge beside a canonical value means an older build
  // removed the state (for example during logout), so defaults win.

  const revision = Math.max(
    currentValid?.revision ?? 0,
    legacyValid?.revision ?? 0
  );
  const serializedValue = JSON.stringify(value);
  const bridgeIsSynchronized =
    currentValid !== undefined &&
    legacyValid !== undefined &&
    legacyValid.isCurrentBridge &&
    currentValid.revision === legacyValid.revision &&
    currentSerializedValue === legacySerializedValue;

  return {
    value,
    expiresAt,
    revision,
    serializedValue: bridgeIsSynchronized ? serializedValue : undefined,
    writeBlocked: false,
    errors,
  };
}

function createCompatibilityValue<S>(
  state: S,
  metadata: CompatibilityMetadata,
  expiresAt?: number
): Record<string, unknown> {
  const stateValue = isRecord(state) ? state : { value: state };

  return {
    ...stateValue,
    [COMPATIBILITY_METADATA_KEY]: metadata,
    ...(expiresAt ? { __expirationTimestamp: expiresAt } : undefined),
  };
}

export default function usePersistentReducer<S, A, I>(
  reducer: Reducer<S, A>,
  initialState: S,
  init: (arg?: I) => S,
  options: UsePersistentReducerOptionsType
): [S, Dispatch<A>] {
  const storageType = options.storageType ?? 'localStorage';
  const [hydration] = useState<HydrationResult<S>>(() =>
    hydrateFromStorage<S, I>({
      enabled: options.enabled,
      key: options.key,
      storageType,
      init,
      fallback: initialState,
    })
  );
  const [state, dispatch] = useReducer(reducer, hydration.value);
  const lastSerializedState = useRef(hydration.serializedValue);
  const expiration = useRef(hydration.expiresAt);
  const revision = useRef(hydration.revision);

  useEffect(() => {
    for (const error of hydration.errors) {
      Logger.error('Unable to hydrate persisted reducer state', {
        error,
        key: options.key,
      });
    }
  }, [hydration.errors, options.key]);

  useEffect(() => {
    if (!options.enabled || hydration.writeBlocked) {
      return;
    }

    let serializedState: string;
    try {
      serializedState = JSON.stringify(state);
    } catch (error) {
      Logger.error('Unable to serialize persisted reducer state', {
        error,
        key: options.key,
      });
      return;
    }

    if (serializedState === lastSerializedState.current) {
      return;
    }

    const capturedGeneration = getPersistenceGeneration();

    function persistState() {
      if (!isPersistenceWriteAllowed(capturedGeneration)) {
        return;
      }

      const storage = getStorage(storageType);
      if (!storage) {
        return;
      }

      try {
        const effectiveExpiresAt =
          options.expiresAt ??
          (storageType === 'sessionStorage'
            ? ExpirationInterval.A_DAY
            : undefined);

        if (effectiveExpiresAt && !expiration.current) {
          expiration.current = getExpirationTime(effectiveExpiresAt);
        }

        const nextRevision = revision.current + 1;
        const writtenAt = Date.now();
        const metadata: CompatibilityMetadata = {
          schema: PERSISTENCE_VERSION,
          revision: nextRevision,
          writtenAt,
        };
        const envelope: PersistedEnvelope<S> = {
          kind: PERSISTENCE_KIND,
          version: PERSISTENCE_VERSION,
          revision: nextRevision,
          writtenAt,
          value: state,
          ...(expiration.current
            ? { expiresAt: expiration.current }
            : undefined),
        };

        storage.setItem(
          options.key,
          JSON.stringify(
            createCompatibilityValue(state, metadata, expiration.current)
          )
        );
        if (!isPersistenceWriteAllowed(capturedGeneration)) {
          return;
        }
        storage.setItem(
          getCurrentPersistenceKey(options.key),
          JSON.stringify(envelope)
        );
        revision.current = nextRevision;
        lastSerializedState.current = serializedState;
      } catch (error) {
        Logger.error('Unable to persist reducer state', {
          error,
          key: options.key,
        });
      }
    }

    const writeDelayMs = options.writeDelayMs ?? DEFAULT_WRITE_DELAY_MS;
    if (writeDelayMs <= 0) {
      persistState();
      return;
    }

    const timeout = { id: undefined as number | undefined };
    const cancel = () => {
      if (timeout.id !== undefined) {
        window.clearTimeout(timeout.id);
      }
    };
    const unregister = registerPendingPersistenceWrite(cancel);
    if (!isPersistenceWriteAllowed(capturedGeneration)) {
      unregister();
      return;
    }

    timeout.id = window.setTimeout(() => {
      unregister();
      persistState();
    }, writeDelayMs);

    return () => {
      cancel();
      unregister();
    };
  }, [
    hydration.writeBlocked,
    options.enabled,
    options.expiresAt,
    options.key,
    options.writeDelayMs,
    state,
    storageType,
  ]);

  return [state, dispatch];
}
